import type { SupabaseClient } from '../../lib/supabase';
import type { Database } from '../../types/database.types';

export type CleanupTable =
  'leads' | 'prospects' | 'lead_lists' | 'lead_list_memberships' | 'copy_sets';
export type JourneyIntent =
  | { kind: 'cleanup'; table: CleanupTable; id: string; label: string }
  | { kind: 'merge'; keeperId: string; loserId: string; label: string };
export type JourneyCommand = Readonly<
  JourneyIntent & { reason: string; requestId: string; actorId: string }
>;

export class JourneyRefusal extends Error {}

/** A retry reuses this object, including the actor-bound receipt UUID. An
 * edited reason or a different record must be a new explicit action. */
export function journeyCommand(
  intent: JourneyIntent,
  reason: string,
  requestId: string,
  actorId: string,
): JourneyCommand {
  if (!actorId) throw new Error('journey_not_permitted');
  if (!reason.trim() || !requestId) throw new Error('journey_reason_required');
  if (intent.kind === 'merge' && intent.keeperId === intent.loserId)
    throw new Error('journey_merge_needs_two_leads');
  return Object.freeze({ ...intent, reason: reason.trim(), requestId, actorId });
}

export async function runJourneyCommand(client: SupabaseClient, command: JourneyCommand) {
  const response =
    command.kind === 'merge'
      ? await client.rpc('merge_leads', {
          p_actor: command.actorId,
          p_keeper: command.keeperId,
          p_loser: command.loserId,
          p_request: command.requestId,
          p_reason: command.reason,
        } satisfies Database['public']['Functions']['merge_leads']['Args'])
      : await client.rpc('cleanup_journey_record', {
          p_actor: command.actorId,
          p_table: command.table,
          p_id: command.id,
          p_request: command.requestId,
          p_reason: command.reason,
        } satisfies Database['public']['Functions']['cleanup_journey_record']['Args']);
  if (response.error) {
    // A SQL exception rolls back this invocation. A concurrent same-request
    // owner is different: its commit is still unknown, so its receipt must stay.
    if (
      ['P0001', '42501', '23505', '55P03', '57014'].includes(response.error.code) &&
      !response.error.message.includes('journey_request_in_progress')
    )
      throw new JourneyRefusal(response.error.message);
    throw new Error(response.error.message);
  }
  const result = response.data;
  if (result === null || typeof result !== 'object' || Array.isArray(result))
    throw new Error('journey_operation_unconfirmed');
  if (command.kind === 'merge') {
    if (result['keeper_id'] !== command.keeperId || result['retired_id'] !== command.loserId)
      throw new Error('journey_operation_unconfirmed');
  } else if (
    result['id'] !== command.id ||
    (command.table === 'copy_sets'
      ? result['archived'] !== true || result['deleted'] !== false
      : result['deleted'] !== true)
  )
    throw new Error('journey_operation_unconfirmed');
  return result;
}

export function journeyOperationError(message: string): string {
  if (message.includes('actor_changed'))
    return 'The signed-in account changed. Check this action with the original account.';
  if (message.includes('pending_unavailable'))
    return 'Saved retry details could not be read. No new request was sent.';
  if (message.includes('cleared_touches'))
    return 'One of these leads has a manually cleared campaign box. The merge was refused; neither record changed.';
  if (message.includes('blocked_by_consent'))
    return 'A campaign membership cannot move under the contact rules. This merge changed nothing.';
  if (message.includes('first_email_in_flight'))
    return 'An email is currently being sent for one of these leads. Wait for it to finish before merging.';
  if (message.includes('campaign_state_conflict') || message.includes('duplicate key'))
    return 'The campaign or round histories conflict. This merge changed nothing; review both records.';
  if (
    message.includes('not_permitted') ||
    message.includes('needs_admin') ||
    message.includes('permission denied')
  )
    return 'You do not have permission to change every affected record. Nothing was changed by this action.';
  if (
    message.includes('not_ready') ||
    message.includes('function') ||
    message.includes('protected_mutation')
  )
    return 'The safe action is not available yet. Refresh the app or ask an admin. No direct-delete fallback is used.';
  return 'The action could not be confirmed. Retry the same request to check whether it completed.';
}
